import axios from 'axios';

// #development
const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  getProfile: () => api.get('/auth/profile'),
};

// ─── Users ─────────────────────────────────────────────────────────────────
export const usersAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    role?: string;
  }) => api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: {
    username: string;
    email: string;
    password: string;
    role?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    job_title?: string;
    department?: string;
    status?: string;
  }) => api.post('/users', data),
  update: (id: string, data: {
    username?: string;
    email?: string;
    password?: string;
    role?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    job_title?: string;
    department?: string;
    status?: string;
  }) => api.put(`/users/${id}`, data),
  updateStatus: (id: string, status: string) =>
    api.patch(`/users/${id}/status`, { status }),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// ─── Organization ──────────────────────────────────────────────────────────
export const organizationAPI = {
  get: () => api.get('/organization'),
  create: (data: {
    name: string;
    domain: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    city?: string;
    country?: string;
  }) => api.post('/organization', data),
  update: (data: {
    name?: string;
    logo?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    city?: string;
    country?: string;
    settings?: any;
  }) => api.put('/organization', data),
};

// ─── Categories ────────────────────────────────────────────────────────────
export const categoriesAPI = {
  getAll: (params?: { applies_to?: string; active?: boolean }) =>
    api.get('/categories', { params }),
  getById: (id: string) => api.get(`/categories/${id}`),
  create: (data: {
    name: string;
    description?: string;
    applies_to: string[];
    color?: string;
  }) => api.post('/categories', data),
  update: (id: string, data: {
    name?: string;
    description?: string;
    applies_to?: string[];
    color?: string;
    is_active?: boolean;
  }) => api.put(`/categories/${id}`, data),
  delete: (id: string) => api.delete(`/categories/${id}`),
  toggle: (id: string) => api.patch(`/categories/${id}/toggle`),
};

// ─── Bids (IDs are UUIDs/strings) ─────────────────────────────────────────
export const bidsAPI = {
  getAll: (category?: string) =>
    api.get('/bids', { params: category ? { category } : {} }),
  getById: (id: string) => api.get(`/bids/${id}`),
  create: (formData: FormData) =>
    api.post('/bids', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/bids/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id: string) => api.delete(`/bids/${id}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/bids/documents/${documentId}`),
};

// ─── Blogs ─────────────────────────────────────────────────────────────────
export const blogsAPI = {
  getAll: (filters?: { status?: string; category?: string }) =>
    api.get('/blogs', {
      params: {
        ...(filters?.status ? { status: filters.status } : {}),
        ...(filters?.category ? { category: filters.category } : {}),
      },
    }),
  getByIdOrSlug: (identifier: string) => api.get(`/blogs/${identifier}`),
  create: (formData: FormData) =>
    api.post('/blogs', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/blogs/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id: string) => api.delete(`/blogs/${id}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/blogs/documents/${documentId}`),
};

// ─── Vacancies ─────────────────────────────────────────────────────────────
export const vacanciesAPI = {
  getAll: () => api.get('/vacancies'),
  getById: (id: string) => api.get(`/vacancies/${id}`),
  create: (formData: FormData) =>
    api.post('/vacancies', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/vacancies/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id: string) => api.delete(`/vacancies/${id}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/vacancies/documents/${documentId}`),
};

// ─── Bursaries ─────────────────────────────────────────────────────────────
export const bursariesAPI = {
  getAll: () => api.get('/bursaries'),
  getById: (id: string) => api.get(`/bursaries/${id}`),
  create: (formData: FormData) =>
    api.post('/bursaries', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/bursaries/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id: string) => api.delete(`/bursaries/${id}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/bursaries/documents/${documentId}`),
};

// ─── Gallery ───────────────────────────────────────────────────────────────
export const galleryAPI = {
  getAll: (type?: string) =>
    api.get('/gallery', { params: type ? { type } : {} }),
  getById: (id: string) => api.get(`/gallery/${id}`),
  create: (formData: FormData) =>
    api.post('/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, data: any) => api.put(`/gallery/${id}`, data),
  delete: (id: string) => api.delete(`/gallery/${id}`),
};

// ─── Projects ──────────────────────────────────────────────────────────────
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id: string) => api.get(`/projects/${id}`),
  create: (formData: FormData) =>
    api.post('/projects', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/projects/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id: string) => api.delete(`/projects/${id}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/projects/documents/${documentId}`),
};

export default api;